/**
 * Auth API - signup, authenticatedFetch, etc.
 */
import { auth } from '../auth/firebase';
import { debugApiRequest, debugApiResponse, debugAuth, isDebugEnabled } from '../debug/debugLogger';
import { getApiBaseUrl, shouldSkipBearerToken } from './apiBase';
import { beginWork, endWork } from '../utils/appUpdate';

const SKIP_AUTH = shouldSkipBearerToken();

async function doFetch(url, options = {}) {
  const headers = { ...options.headers };
  const hasBody = options.body != null && options.body !== '';
  if (hasBody && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (SKIP_AUTH) {
    return fetch(url, { ...options, headers });
  }
  return authenticatedFetch(url, { ...options, headers });
}

/**
 * API fetch: when REACT_APP_SKIP_AUTH=true, plain fetch (no Bearer). Backend must have SKIP_AUTH_FOR_DEV=true.
 * Otherwise uses authenticatedFetch with Bearer token.
 * When REACT_APP_DEBUG=true, logs all requests/responses for CSV export.
 */
export async function apiFetch(url, options = {}) {
  const method = options.method || 'GET';
  const body = options.body;
  const debugOn = isDebugEnabled();
  if (debugOn) {
    debugApiRequest(url, method, body ? String(body).slice(0, 100) : null);
  }
  const start = performance.now();
  // A write in flight must not be interrupted by the app-update reload (utils/appUpdate.js).
  // Reads are harmless to cut. Streams are additionally tracked by their reader loop.
  const tracked = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
  if (tracked) beginWork();
  try {
    const res = await doFetch(url, options);
    if (debugOn) {
      const durationMs = Math.round(performance.now() - start);
      let resBody = '';
      try {
        resBody = await res.clone().text();
      } catch {}
      debugApiResponse(url, method, res.status, durationMs, null, resBody.slice(0, 200));
    }
    return res;
  } catch (err) {
    if (debugOn) {
      debugApiResponse(url, method, null, Math.round(performance.now() - start), err);
    }
    throw err;
  } finally {
    if (tracked) endWork();
  }
}

/**
 * Fetch with Bearer token. On 401: refresh token, retry once; still 401 → signOut (ProtectedRoute redirects).
 */
export async function authenticatedFetch(url, options = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  let token = await user.getIdToken();
  const headers = { ...options.headers, Authorization: `Bearer ${token}` };
  let response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      try {
        token = await user.getIdToken(true);
      } catch {
        debugAuth('signout_token_refresh_failed', url);
        try { sessionStorage.setItem('sessionExpired', '1'); } catch {}
        await auth.signOut();
        throw new Error('Session expired');
      }
      response = await fetch(url, { ...options, headers: { ...options.headers, Authorization: `Bearer ${token}` } });
      if (response.status === 401) {
        debugAuth('signout_401', url);
        try { sessionStorage.setItem('sessionExpired', '1'); } catch {}
        await auth.signOut();
        throw new Error('Session expired');
      }
    }
  return response;
}

export async function fetchMyIdentity() {
  const res = await apiFetch(`${getApiBaseUrl()}/auth/me`);
  if (!res.ok) throw new Error('プロフィールを取得できませんでした');
  return res.json();
}

export async function bindMyIdentity({ stylistNamePos, homeStore, noPosData = false }) {
  const res = await apiFetch(`${getApiBaseUrl()}/auth/me`, {
    method: 'PATCH',
    body: JSON.stringify({
      stylist_name_pos: stylistNamePos,
      home_store: homeStore,
      // 「POSにまだ名前がありません」: hand-typed name, server skips the POS existence check
      no_pos_data: noPosData,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'プロフィールを保存できませんでした');
  }
  return res.json();
}

/** 設定 → 所属店舗. Moves only the main store; POS name, goals and to-dos stay with the person. */
export async function changeMyHomeStore(homeStore) {
  const res = await apiFetch(`${getApiBaseUrl()}/auth/me/home-store`, {
    method: 'PATCH',
    body: JSON.stringify({ home_store: homeStore }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || '所属店舗を変更できませんでした');
  }
  return res.json();
}

/** 設定 → 氏 / 名 (own account). The AI calls the person by 名; the POS name is not changed. */
export async function saveMyName({ familyName, givenName }) {
  const res = await apiFetch(`${getApiBaseUrl()}/auth/me/name`, {
    method: 'PATCH',
    body: JSON.stringify({ family_name: familyName, given_name: givenName }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || '氏名を保存できませんでした');
  }
  return res.json();
}

/** company_admin only: 経営者 without a POS record. Overlay never returns afterwards. */
export async function declareNotStylist() {
  const res = await apiFetch(`${getApiBaseUrl()}/auth/me/not-stylist`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || '保存できませんでした');
  }
  return res.json();
}

